// Portal authentication utilities for seamless switching between HRMS and Onboarding portals

/**
 * Handle authentication token from portal switch
 * This function should be called on app initialization to check for portal switch tokens
 */
export const handlePortalSwitchAuth = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const authToken = urlParams.get('auth_token');
  const isPortalSwitch = urlParams.get('portal_switch');

  if (authToken && isPortalSwitch === 'true') {
    // Store the token from the other portal
    localStorage.setItem('token', authToken);
    
    // Clean up the URL
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);
    
    return true; // Indicates successful portal switch
  }
  
  return false;
};

/**
 * Get portal configuration based on environment
 */
export const getPortalConfig = () => {
  return {
    hrms: {
      name: 'HRMS Portal',
      description: 'Human Resource Management',
      url: process.env.REACT_APP_HRMS_URL || 'http://localhost:3000',
      current: true // This will be true for HRMS portal
    },
    onboarding: {
      name: 'Onboarding Portal', 
      description: 'Employee Onboarding',
      url:
        process.env.REACT_APP_ONBOARDING_URL ||
        process.env.REACT_APP_REDIRECT_TO_ONBOARDING ||
        'http://localhost:5173',
      current: false
    }
  };
};

/**
 * Switch to another portal with authentication
 */
export const switchPortal = async (targetPortal) => {
  const config = getPortalConfig();
  const portal = config[targetPortal];
  
  if (!portal) {
    throw new Error(`Unknown portal: ${targetPortal}`);
  }
  
  // Get current auth token
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  
  // Construct URL with token for seamless authentication
  const targetUrl = new URL(portal.url);
  if (token) {
    targetUrl.searchParams.set('auth_token', token);
    targetUrl.searchParams.set('portal_switch', 'true');
    targetUrl.searchParams.set('source_portal', 'hrms');
  }
  
  // Switch to the other portal in the same tab
  window.location.href = targetUrl.toString();
};

/**
 * Check if user has access to multiple portals
 */
export const hasMultiPortalAccess = (user) => {
  // For now, all authenticated users have access to both portals
  // This can be enhanced with role-based access control
  return user && user.id;
};

/**
 * Get shared user data for portal switching
 */
export const getSharedUserData = (user) => {
  return {
    id: user?.id,
    email: user?.email_address || user?.email,
    firstName: user?.first_name,
    lastName: user?.last_name,
    role: user?.role,
    department: user?.department,
    // Add other fields that should be shared between portals
  };
};

/**
 * Validate portal switch token
 */
export const validatePortalToken = async (token) => {
  try {
    // This would typically validate the token with the backend
    // For now, we'll do a simple check
    if (!token || token.length < 10) {
      return false;
    }
    
    // You can add API call here to validate token
    // const response = await api.post('/auth/validate-portal-token', { token });
    // return response.data.valid;
    
    return true;
  } catch (error) {
    console.error('Error validating portal token:', error);
    return false;
  }
};

/**
 * Portal detection utilities
 */
export const detectCurrentPortal = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  const pathname = window.location.pathname;
  
  // Detect based on URL patterns
  if (pathname.includes('/onboarding') || port === '3001') {
    return 'onboarding';
  }
  
  return 'hrms'; // Default to HRMS
};

/**
 * Initialize portal authentication on app start
 */
export const initializePortalAuth = () => {
  // Handle portal switch authentication
  const switchSuccess = handlePortalSwitchAuth();
  
  // Detect current portal
  const currentPortal = detectCurrentPortal();
  
  return {
    switchSuccess,
    currentPortal
  };
};

export default {
  handlePortalSwitchAuth,
  getPortalConfig,
  switchPortal,
  hasMultiPortalAccess,
  getSharedUserData,
  validatePortalToken,
  detectCurrentPortal,
  initializePortalAuth
};